import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, User, Building, Calendar,
  FileText, MessageSquare, Send, AlertTriangle
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';

const RequestDetailsPage = () => {
  const { requestId } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);
  const requestType = location.state?.type || 'leave';

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchRequestDetails();
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      const endpoint = `/hr-v2/requests/${requestType}/${requestId}`;
      const response = await api.get(endpoint);
      setRequest(response.data);
    } catch (error) {
      // If single endpoint fails, try to get from list
      try {
        const listEndpoint = `/hr-v2/requests/${requestType}`;
        const listResponse = await api.get(listEndpoint);
        const found = listResponse.data.find(r => r.id === requestId);
        if (found) {
          setRequest(found);
        } else {
          toast.error(i18n.language === 'ar' ? 'فشل تحميل الطلب' : 'Failed to load request');
          navigate('/hr/requests');
        }
      } catch (err) {
        toast.error(i18n.language === 'ar' ? 'فشل تحميل الطلب' : 'Failed to load request');
        navigate('/hr/requests');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!comments.trim()) {
      toast.error(i18n.language === 'ar' ? 'يرجى إضافة تعليق' : 'Please add comments');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post('/hr-v2/requests/approve', {
        request_id: requestId,
        request_type: requestType,
        action: 'approve',
        comments: comments
      });
      toast.success(i18n.language === 'ar' ? 'تمت الموافقة على الطلب' : 'Request approved successfully');
      fetchRequestDetails();
      setComments('');
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل في معالجة الطلب' : 'Failed to process request');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      toast.error(i18n.language === 'ar' ? 'يرجى إضافة سبب الرفض' : 'Please add rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post('/hr-v2/requests/approve', {
        request_id: requestId,
        request_type: requestType,
        action: 'reject',
        comments: comments
      });
      toast.success(i18n.language === 'ar' ? 'تم رفض الطلب' : 'Request rejected');
      fetchRequestDetails();
      setComments('');
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل في معالجة الطلب' : 'Failed to process request');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { label: i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending', className: 'bg-yellow-500 text-white', icon: Clock },
      approved: { label: i18n.language === 'ar' ? 'موافق' : 'Approved', className: 'bg-green-500 text-white', icon: CheckCircle },
      rejected: { label: i18n.language === 'ar' ? 'مرفوض' : 'Rejected', className: 'bg-red-500 text-white', icon: XCircle }
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.className} gap-1.5`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </Badge>
    );
  };

  const canApprove = () => {
    if (!request || user.role !== 'admin') return false;
    return request.status === 'pending';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!request) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/hr/requests')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              {i18n.language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{request.request_number}</h1>
                {getStatusBadge(request.status)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Request Details */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'تفاصيل الطلب' : 'Request Details'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'الموظف' : 'Employee'}</p>
                      <p className="font-semibold flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {request.employee_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'القسم' : 'Department'}</p>
                      <p className="font-semibold flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        {request.department}
                      </p>
                    </div>
                  </div>

                  {/* Leave Request Specific */}
                  {requestType === 'leave' && (
                    <>
                      <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'نوع الإجازة' : 'Leave Type'}</p>
                          <p className="font-semibold">{request.leave_type}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'تاريخ البداية' : 'Start Date'}</p>
                          <p className="font-semibold">{format(new Date(request.start_date), 'dd/MM/yyyy')}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'تاريخ النهاية' : 'End Date'}</p>
                          <p className="font-semibold">{format(new Date(request.end_date), 'dd/MM/yyyy')}</p>
                        </div>
                      </div>
                      <div className="pt-4 border-t">
                        <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'السبب' : 'Reason'}</p>
                        <p className="text-gray-700">{request.reason}</p>
                      </div>
                      {request.substitute_employee_name && (
                        <div className="pt-4 border-t">
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'الموظف البديل' : 'Substitute'}</p>
                          <p className="font-semibold">{request.substitute_employee_name}</p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Advance Request Specific */}
                  {requestType === 'advance' && (
                    <>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'المبلغ' : 'Amount'}</p>
                          <p className="font-bold text-2xl text-green-600">{request.amount?.toLocaleString()} {i18n.language === 'ar' ? 'ريال' : 'SAR'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'فترة السداد' : 'Repayment Period'}</p>
                          <p className="font-semibold">{request.repayment_months} {i18n.language === 'ar' ? 'شهر' : 'months'}</p>
                        </div>
                      </div>
                      <div className="pt-4 border-t">
                        <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'السبب' : 'Reason'}</p>
                        <p className="text-gray-700">{request.reason}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Approval Timeline */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'مسار الموافقة' : 'Approval Timeline'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {request.approval_workflow && request.approval_workflow.length > 0 ? (
                    <div className="space-y-4">
                      {request.approval_workflow.map((level, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              level.status === 'approved' ? 'bg-green-500' :
                              level.status === 'rejected' ? 'bg-red-500' :
                              'bg-gray-300'
                            }`}>
                              {level.status === 'approved' ? (
                                <CheckCircle className="w-5 h-5 text-white" />
                              ) : level.status === 'rejected' ? (
                                <XCircle className="w-5 h-5 text-white" />
                              ) : (
                                <Clock className="w-5 h-5 text-white" />
                              )}
                            </div>
                            {index < request.approval_workflow.length - 1 && (
                              <div className="w-0.5 h-16 bg-gray-300" />
                            )}
                          </div>
                          <div className="flex-1 pb-8">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <h4 className="font-bold text-gray-900">{level.approver_name}</h4>
                                <p className="text-sm text-gray-600">{level.approver_role}</p>
                              </div>
                              <Badge className={level.status === 'approved' ? 'bg-green-500' : level.status === 'rejected' ? 'bg-red-500' : 'bg-yellow-500'}>
                                {level.status === 'approved' ? (i18n.language === 'ar' ? 'موافق' : 'Approved') :
                                 level.status === 'rejected' ? (i18n.language === 'ar' ? 'مرفوض' : 'Rejected') :
                                 (i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending')}
                              </Badge>
                            </div>
                            {level.comments && (
                              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{level.comments}</p>
                            )}
                            {level.approved_at && (
                              <p className="text-xs text-gray-500 mt-2">
                                {format(new Date(level.approved_at), 'dd/MM/yyyy HH:mm')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-center py-8">
                      {i18n.language === 'ar' ? 'لا يوجد مسار موافقة' : 'No approval workflow'}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Approval Actions (Admin Only) */}
              {canApprove() && (
                <Card className="shadow-lg border-2 border-blue-200">
                  <CardHeader>
                    <CardTitle>{i18n.language === 'ar' ? 'إجراءات الموافقة' : 'Approval Actions'}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="comments">{i18n.language === 'ar' ? 'التعليقات' : 'Comments'} *</Label>
                      <Textarea
                        id="comments"
                        rows={3}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder={i18n.language === 'ar' ? 'أضف تعليقك...' : 'Add your comments...'}
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleReject}
                        disabled={processing}
                        variant="outline"
                        className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        {i18n.language === 'ar' ? 'رفض' : 'Reject'}
                      </Button>
                      <Button
                        onClick={handleApprove}
                        disabled={processing}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {i18n.language === 'ar' ? 'موافقة' : 'Approve'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'معلومات إضافية' : 'Additional Info'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">{i18n.language === 'ar' ? 'رقم الطلب' : 'Request Number'}</span>
                    <span className="font-semibold">{request.request_number}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">{i18n.language === 'ar' ? 'الحالة' : 'Status'}</span>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{i18n.language === 'ar' ? 'تاريخ الإنشاء' : 'Created At'}</span>
                    <span className="text-sm font-semibold">
                      {format(new Date(request.created_at), 'dd/MM/yyyy')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RequestDetailsPage;